import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ticketCreateSchema } from '@ventin/shared';
import type { TicketPriority, TicketCategory } from '@ventin/shared';
import { useCreateTicket } from '@/lib/adminApi';
import { useClientWebsites } from '@/lib/clientApi';
import { uploadAttachment } from '@/lib/attachmentUpload';
import { PRIORITY_OPTIONS, CATEGORY_OPTIONS } from '@/components/tickets/ticketMeta';
import { ScreenshotCaptureFlow } from '@/components/screenshot/ScreenshotCaptureFlow';
import type { ScreenshotResult } from '@/components/screenshot/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

/** Max screenshots stageable before ticket creation (plan §5.5). */
const MAX_SHOTS = 10;

interface StagedShot {
  key: string;
  result: ScreenshotResult;
  /** Object URL for the thumbnail; revoked on remove/unmount/upload. */
  url: string;
}

/**
 * NewTicketPage — /portal/tickets/new
 *
 * TKT-01 portal submission form. Clients create a ticket by providing a
 * title and description. companyId is NEVER sent in the request body —
 * the API forces it from req.authUser.companyId (TKT-01 trust boundary).
 */
export default function NewTicketPage() {
  const navigate = useNavigate();
  const createTicket = useCreateTicket();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<TicketPriority>('NORMAL');
  const [category, setCategory] = useState<TicketCategory>('CONTENT_UPDATE');
  const [websiteId, setWebsiteId] = useState('');
  const [titleError, setTitleError] = useState<string | null>(null);
  const [descriptionError, setDescriptionError] = useState<string | null>(null);

  const [staged, setStaged] = useState<StagedShot[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const keyCounter = useRef(0);
  // Set once the ticket is created so a retry after a partial upload failure
  // never creates a duplicate ticket.
  const createdTicketIdRef = useRef<string | null>(null);

  // Offer a website picker only when the company has more than one site.
  const { data: websitesData } = useClientWebsites();
  const websites = websitesData?.websites ?? [];

  // Revoke any outstanding thumbnail object URLs on unmount.
  const stagedRef = useRef<StagedShot[]>([]);
  stagedRef.current = staged;
  useEffect(() => () => stagedRef.current.forEach((s) => URL.revokeObjectURL(s.url)), []);

  // Submit button enabled only when both fields have content
  const canSubmit = title.trim().length > 0 && description.trim().length > 0;
  const hasCreatedTicket = createdTicketIdRef.current !== null;

  function stageScreenshot(result: ScreenshotResult) {
    setStaged((prev) => {
      if (prev.length >= MAX_SHOTS) {
        toast(`You can attach up to ${MAX_SHOTS} screenshots`);
        return prev;
      }
      const key = `shot-${keyCounter.current++}`;
      return [...prev, { key, result, url: URL.createObjectURL(result.blob) }];
    });
  }

  function removeStaged(key: string) {
    setStaged((prev) => {
      const found = prev.find((s) => s.key === key);
      if (found) URL.revokeObjectURL(found.url);
      return prev.filter((s) => s.key !== key);
    });
  }

  /**
   * Upload staged screenshots one at a time. Succeeded shots are dropped;
   * failures stay staged so the user can retry just those (plan §5.5). Returns
   * the shots that failed.
   */
  async function uploadStaged(ticketId: string, shots: StagedShot[]): Promise<StagedShot[]> {
    const failed: StagedShot[] = [];
    for (const shot of shots) {
      try {
        await uploadAttachment(ticketId, shot.result);
        URL.revokeObjectURL(shot.url);
      } catch {
        failed.push(shot);
        toast.error('A screenshot failed to upload — you can retry it.');
      }
    }
    return failed;
  }

  async function finishOrRetry(ticketId: string, shots: StagedShot[]) {
    const failed = await uploadStaged(ticketId, shots);
    setStaged(failed);
    if (failed.length === 0) {
      toast.success('Ticket submitted');
      navigate('/portal/tickets/' + ticketId);
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();

    // Retry path: ticket already exists, only re-upload the failed screenshots.
    const existingId = createdTicketIdRef.current;
    if (existingId) {
      setSubmitting(true);
      try {
        await finishOrRetry(existingId, staged);
      } finally {
        setSubmitting(false);
      }
      return;
    }

    // Client-side validation via shared schema
    const result = ticketCreateSchema.safeParse({
      title,
      description,
      priority,
      category,
      ...(websiteId ? { websiteId } : {}),
    });
    if (!result.success) {
      const fieldErrors = result.error.flatten().fieldErrors;
      setTitleError(fieldErrors.title?.[0] ?? null);
      setDescriptionError(fieldErrors.description?.[0] ?? null);
      return;
    }

    setTitleError(null);
    setDescriptionError(null);
    setSubmitting(true);
    try {
      // TKT-01 trust boundary: companyId is NOT sent — API enforces it from claims.
      // Pass the schema-parsed data (which carries source/isBillable/usesIncrements
      // defaults) but destructure to explicitly exclude companyId.
      const { companyId: _omit, ...ticketInput } = result.data;
      const { ticket } = await createTicket.mutateAsync(ticketInput);
      createdTicketIdRef.current = ticket.id;
      await finishOrRetry(ticket.id, staged);
    } catch {
      toast("Couldn't submit — try again");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="max-w-2xl">
      <header className="mb-6">
        <h2 className="text-2xl font-semibold leading-[1.3]">New Ticket</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Describe your request and we&apos;ll get back to you shortly.
        </p>
      </header>

      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="space-y-1.5">
          <Label htmlFor="ticket-title">Title</Label>
          <Input
            id="ticket-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Brief summary of your request"
            aria-describedby={titleError ? 'title-error' : undefined}
          />
          {titleError && (
            <p id="title-error" className="text-sm text-destructive">
              {titleError}
            </p>
          )}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="ticket-description">Description</Label>
          <textarea
            id="ticket-description"
            rows={8}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Please provide as much detail as possible"
            aria-describedby={descriptionError ? 'description-error' : undefined}
            className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
          />
          {descriptionError && (
            <p id="description-error" className="text-sm text-destructive">
              {descriptionError}
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="ticket-priority">Priority</Label>
            <select
              id="ticket-priority"
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              value={priority}
              onChange={(e) => setPriority(e.target.value as TicketPriority)}
            >
              {PRIORITY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ticket-category">Category</Label>
            <select
              id="ticket-category"
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              value={category}
              onChange={(e) => setCategory(e.target.value as TicketCategory)}
            >
              {CATEGORY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {websites.length > 1 && (
          <div className="space-y-1.5">
            <Label htmlFor="ticket-website">Website (optional)</Label>
            <select
              id="ticket-website"
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              value={websiteId}
              onChange={(e) => setWebsiteId(e.target.value)}
            >
              <option value="">Not sure / all sites</option>
              {websites.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.label || w.url}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Screenshots: capture/annotate and stage locally before submit. */}
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-3">
            <Label>Screenshots (optional)</Label>
            <span className="text-xs text-muted-foreground">
              {staged.length}/{MAX_SHOTS}
            </span>
          </div>
          <ScreenshotCaptureFlow
            onComplete={stageScreenshot}
            disabled={submitting || staged.length >= MAX_SHOTS}
          />
          {staged.length > 0 && (
            <ul className="grid grid-cols-3 gap-3 sm:grid-cols-4">
              {staged.map((s) => (
                <li key={s.key} className="relative">
                  <img
                    src={s.url}
                    alt={s.result.comment || 'Staged screenshot'}
                    className="aspect-video w-full rounded-md border object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => removeStaged(s.key)}
                    disabled={submitting}
                    aria-label="Remove screenshot"
                    className="absolute right-1 top-1 rounded-full bg-black/70 px-1.5 text-xs text-white hover:bg-black disabled:opacity-50"
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {hasCreatedTicket && staged.length > 0 && (
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm">
            <p className="font-medium text-amber-900">
              Your ticket was created, but {staged.length} screenshot
              {staged.length > 1 ? 's' : ''} failed to upload.
            </p>
            <button
              type="button"
              onClick={() => navigate('/portal/tickets/' + createdTicketIdRef.current)}
              className="mt-1 text-primary underline underline-offset-4 hover:opacity-80"
            >
              Continue to ticket without them
            </button>
          </div>
        )}

        <Button type="submit" disabled={!canSubmit || submitting}>
          {submitting
            ? 'Submitting…'
            : hasCreatedTicket
              ? 'Retry screenshot upload'
              : 'Submit ticket'}
        </Button>
      </form>
    </div>
  );
}
